import React, { useEffect, useState } from "react";
import { Table, Form, Image } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery } from "@apollo/client";
import useReactRouter from "use-react-router";
import moment from "moment";
import _ from "lodash";

import { COLOR_APP, BRAND_RED, COLOR_TEXT } from "../../constants";
import { thousandSeperate } from "../../helpers";
import { GET_CF_CUSTOMERS } from "../../graphql/customer/query";
import EmptyComponent from "../../components/EmptyComponent";
import LoadingSpinner from "../../components/LoadingSpinner";
import profileImage from "../../image/profile.png";

const LIMIT = 50;

//* Friendly label + colour for each origin channel. LIVE/CF share a label because
//* "CF" is stored as platform "CF" on the order (the shop-level system is "LIVE").
//* Soft tinted chips from the 4B brand range (red → wine → purple) so they sit with
//* the gradient header instead of fighting it; text is the full-strength shade.
const PLATFORM_META = {
  CF: { label: "CF", color: "#FDE8EC", text: BRAND_RED },
  SALE_PAGE: { label: "ເຊວເພຈ", color: "#F1E4F5", text: COLOR_TEXT },
  POS: { label: "POS", color: "#F6E3EC", text: "#8E1759" },
  AFFILIATE: { label: "ອາຟຟີລີເອດ", color: "#FDF0E1", text: "#B45309" },
};

const PlatformBadges = ({ platforms }) => {
  const list = (platforms || []).filter(Boolean);
  if (list.length === 0) return <span style={{ color: "#999" }}>-</span>;
  return (
    <span>
      {list.map((p) => {
        const meta = PLATFORM_META[p] || {
          label: p,
          color: "#F1F1F4",
          text: "#52525B",
        };
        return (
          <span
            key={p}
            style={{
              display: "inline-block",
              background: meta.color,
              color: meta.text,
              border: `1px solid ${meta.text}33`,
              borderRadius: 999,
              padding: "2px 10px",
              fontSize: 12,
              fontWeight: 600,
              marginRight: 4,
              marginBottom: 2,
            }}
          >
            {meta.label}
          </span>
        );
      })}
    </span>
  );
};

//* Show a money figure only when it is non-zero, with its currency label, so a
//* customer who only ordered in Kip doesn't show "0 ບາດ / 0 ໂດລາ" noise.
const MoneyByCurrency = ({ kip, baht, usd }) => {
  const parts = [];
  if (kip) parts.push(`${thousandSeperate(kip)} ກີບ`);
  if (baht) parts.push(`${thousandSeperate(baht)} ບາດ`);
  if (usd) parts.push(`${thousandSeperate(usd)} ໂດລາ`);
  if (parts.length === 0) return <span>0 ກີບ</span>;
  return (
    <span>
      {parts.map((p, i) => (
        <div key={i}>{p}</div>
      ))}
    </span>
  );
};

function CustomerCFList() {
  const { history } = useReactRouter();
  const SHOP_ID = localStorage.getItem("SHOP");

  const [customerList, setCustomerList] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [platform, setPlatform] = useState(""); //* "" = all channels
  const [skip, setSkip] = useState(0);

  const [getCfCustomers, { data, loading }] = useLazyQuery(GET_CF_CUSTOMERS, {
    fetchPolicy: "cache-and-network",
  });

  const _loadCustomers = (nextSkip = 0) => {
    let variables = {
      shop: SHOP_ID,
      skip: nextSkip,
      limit: LIMIT,
    };
    if (!_.isEmpty(search)) variables.searchKeyWord = search;
    if (!_.isEmpty(startDate)) variables.createdAt_gte = startDate;
    if (!_.isEmpty(endDate)) variables.createdAt_lt = endDate;
    if (!_.isEmpty(platform)) variables.platform = platform;
    getCfCustomers({ variables });
  };

  //* Debounce the search so we don't fire a query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSkip(0);
      _loadCustomers(0);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, startDate, endDate, platform]);

  useEffect(() => {
    if (data?.cfCustomers) {
      setCustomerList(data.cfCustomers.data || []);
      setTotal(data.cfCustomers.total || 0);
    }
  }, [data]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(skip / LIMIT) + 1;

  const _goPage = (page) => {
    const nextSkip = (page - 1) * LIMIT;
    setSkip(nextSkip);
    _loadCustomers(nextSkip);
  };

  const _openDetail = (customer) => {
    //* CF customers open by userFbId; Sale-page customers have no FB id, so fall
    //* back to a phone-based route param. The detail page reads route state
    //* (customerKey / phone / userFbId) to build the right order-history query.
    const routeParam =
      customer.userFbId ||
      (customer.phone ? `phone:${customer.phone}` : customer.customerKey);
    history.push(`/customer-cf-detail/${encodeURIComponent(routeParam)}`, {
      customerKey: customer.customerKey,
      userFbId: customer.userFbId,
      cfName: customer.cfName || customer.customerName,
      phone: customer.phone,
      customerImage: customer.customerImage,
      platforms: customer.platforms,
    });
  };

  return (
    <div>
      <div style={{ height: 10 }} />
      <h3 style={{ color: "#616161" }}>
        <FontAwesomeIcon icon={faUsers} style={{ marginRight: 8 }} />
        ລູກຄ້າສູນລວມທັງໝົດ
      </h3>
      <p
        style={{ fontSize: 14, color: "#616161", margin: 0 }}
        className="text-left"
      >
        ລາຍຊື່ລູກຄ້າທຸກຄົນທີ່ເຄີຍສັ່ງຊື້ນໍາຮ້ານ ທຸກຊ່ອງທາງ (CF / ເຊວເພຈ / POS)
        ພ້ອມຍອດສັ່ງຊື້ ແລະ ປະຫວັດການສັ່ງ
      </p>

      <div style={{ height: 16 }} />
      <div
        style={{
          background: "#F8F4FA",
          border: "1px solid #EEE4F2",
          borderRadius: 8,
          padding: 15,
        }}
      >
        <div className="row">
          <div className="col-md-4">
            <label>ຄົ້ນຫາ (ຊື່ / ເບີໂທ)</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="ຄົ້ນຫາຊື່ ຫຼື ເບີໂທ . . ."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: "absolute",
                  right: 12,
                  top: 12,
                  color: "#999",
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <label>ຊ່ອງທາງ</label>
            <Form.Select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              <option value="">ທັງໝົດ</option>
              <option value="CF">CF</option>
              <option value="SALE_PAGE">ເຊວເພຈ</option>
              <option value="POS">POS</option>
              <option value="AFFILIATE">ອາຟຟີລີເອດ</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <label>ແຕ່ວັນທີ</label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label>ຫາວັນທີ</label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div style={{ height: 12 }} />
      <div style={{ color: "#616161", fontSize: 14 }}>
        ພົບລູກຄ້າທັງໝົດ:{" "}
        <b style={{ color: BRAND_RED }}>{thousandSeperate(total)}</b> ຄົນ
      </div>
      <div style={{ height: 8 }} />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>ລ/ດ</th>
              <th>ຮູບ</th>
              <th>ຊື່ລູກຄ້າ</th>
              <th>ເບີໂທ</th>
              <th>ຊ່ອງທາງ</th>
              <th className="text-center">ຈຳນວນບິນ</th>
              <th className="text-center">ຈຳນວນສິນຄ້າ</th>
              <th>ຍອດສັ່ງຊື້ລວມ</th>
              <th className="text-center">ຈ່າຍແລ້ວ / ຄ້າງ</th>
              <th>CF ລ່າສຸດ</th>
            </tr>
          </thead>
          {customerList?.length > 0 ? (
            <tbody>
              {customerList.map((c, index) => (
                <tr
                  key={c.customerKey || c.userFbId || index}
                  className="hoverme"
                  style={{ cursor: "pointer" }}
                  onClick={() => _openDetail(c)}
                >
                  <td>{skip + index + 1}</td>
                  <td>
                    <Image
                      src={c.customerImage || profileImage}
                      onError={(e) => (e.target.src = profileImage)}
                      style={{
                        height: 36,
                        width: 36,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  </td>
                  <td style={{ color: COLOR_TEXT, fontWeight: "bold" }}>
                    {c.cfName || c.customerName || "-"}
                  </td>
                  <td>{c.phone || "-"}</td>
                  <td>
                    <PlatformBadges platforms={c.platforms} />
                  </td>
                  <td className="text-center">
                    {thousandSeperate(c.orderGroupCount)}
                  </td>
                  <td className="text-center">
                    {thousandSeperate(c.orderCount)}
                  </td>
                  <td>
                    <MoneyByCurrency
                      kip={c.totalPrice}
                      baht={c.sumPriceBaht}
                      usd={c.sumPriceUsd}
                    />
                  </td>
                  <td className="text-center">
                    <span style={{ color: "#15803D", fontWeight: 600 }}>
                      {c.paidCount || 0}
                    </span>
                    {" / "}
                    <span style={{ color: BRAND_RED, fontWeight: 600 }}>
                      {c.unpaidCount || 0}
                    </span>
                  </td>
                  <td>
                    {c.lastCfAt
                      ? moment(c.lastCfAt).format("DD/MM/YYYY HH:mm")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={10}>
                  <EmptyComponent title={"ຍັງບໍ່ມີລູກຄ້າ"} />
                </td>
              </tr>
            </tbody>
          )}
        </Table>
      )}

      {totalPages > 1 && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) =>
                Math.abs(p - currentPage) <= 3 || p === 1 || p === totalPages,
            )
            .map((p) => (
              <button
                key={p}
                onClick={() => _goPage(p)}
                style={{
                  width: 34,
                  height: 34,
                  border:
                    "solid 1px " +
                    (p === currentPage ? "transparent" : "#E4D5EA"),
                  margin: 2,
                  background: p === currentPage ? COLOR_APP : "#fff",
                  color: p === currentPage ? "#fff" : COLOR_TEXT,
                  borderRadius: 4,
                }}
              >
                {p}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default CustomerCFList;
